<template>
  <div class="d-flex flex-column ga-4">
    <div class="position-relative">
      <iframe
        ref="frame"
        allow="
          camera;
          publickey-credentials-get;
          publickey-credentials-create;
          web-share;
          clipboard-write;
        "
        class="deepid-web-frame"
        :class="{ 'deepid-web-frame--fit': !fillsDialog }"
        :src="frameSrc"
        :style="{ height: `${shownHeight}px` }"
        :title="i18n.t(`authorities.${AuthorityService.DEEP_ID}`)"
        @load="isLoaded = true"
      />
      <div
        v-if="isLoading"
        class="d-flex align-center justify-center bg-surface deepid-web-loader"
      >
        <v-progress-circular color="primary" indeterminate size="60" />
      </div>
    </div>
    <DeepIdWebFooter v-if="showFooter" ref="footer" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { useDisplay, useTheme } from 'vuetify';
import DeepIdWebFooter from '@/components/cards/DeepIdWebFooter.vue';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';

const LOADER_HEIGHT = 240;
// gap between frame and footer (ga-4)
const FOOTER_GAP = 16;

const props = withDefaults(
  defineProps<{
    url: string;
    // off when the host dialog shows the footer in its own action bar
    showFooter?: boolean;
  }>(),
  { showFooter: true }
);

const emit = defineEmits<{
  (event: 'done'): void;
}>();

const i18n = useI18n();
const theme = useTheme();
const frame = useTemplateRef<HTMLIFrameElement>('frame');
const footer = useTemplateRef<ComponentPublicInstance>('footer');

// DeepDialog goes fullscreen on xs; there DeepID keeps its own full-height mobile layout (camera screens need it)
const fillsDialog = useDisplay().xs.value;

const isLoaded = ref(false);
const contentHeight = ref<number>();
const fillHeight = ref<number>();

const isLoading = computed(() =>
  fillsDialog ? !isLoaded.value : !contentHeight.value
);

const shownHeight = computed(
  () => (fillsDialog ? fillHeight.value : contentHeight.value) ?? LOADER_HEIGHT
);

const frameSrc = computed(() => {
  const url = new URL(props.url);
  url.searchParams.set('embedOrigin', window.location.origin);
  // the org branding from DeepAdmin may have overridden the theme's primary color
  const primaryColor = theme.current.value.colors.primary;
  if (typeof primaryColor === 'string') {
    url.searchParams.set('embedPrimaryColor', primaryColor);
  }
  if (fillsDialog) {
    url.searchParams.set('embedLayout', 'fill');
  }
  return url.toString();
});

function measureFillHeight() {
  const content = frame.value?.closest<HTMLElement>('.v-card-text');
  const card = content?.closest<HTMLElement>('.v-card');
  if (!frame.value || !content || !card) return;

  const actions = card.querySelector<HTMLElement>(':scope > .v-card-actions');
  const reservedBelowFrame =
    (footer.value ? footer.value.$el.offsetHeight + FOOTER_GAP : 0) +
    parseFloat(getComputedStyle(content).paddingBottom) +
    (actions?.offsetHeight ?? 0);

  fillHeight.value = Math.max(
    LOADER_HEIGHT,
    card.getBoundingClientRect().bottom -
      frame.value.getBoundingClientRect().top -
      reservedBelowFrame
  );
}

if (fillsDialog) {
  useResizeObserver(
    () => frame.value?.closest<HTMLElement>('.v-card') ?? undefined,
    measureFillHeight
  );
}

useEventListener(window, 'message', (event: MessageEvent) => {
  if (
    event.origin !== new URL(props.url).origin ||
    event.source !== frame.value?.contentWindow ||
    event.data?.source !== 'deepid'
  ) {
    return;
  }

  if (event.data.type === 'resize') {
    if (typeof event.data.height === 'number' && event.data.height > 0) {
      contentHeight.value = event.data.height;
    }
    return;
  }

  // DeepSign's existing identification and sign-status polling picks up the outcome
  emit('done');
});
</script>

<style scoped lang="scss">
// covers the frame instead of hiding it: browsers pause rendering in hidden cross-origin frames, so DeepID could not report its height
.deepid-web-loader {
  position: absolute;
  inset: 0;
}

.deepid-web-frame {
  display: block;
  width: 100%;
  border: 0;
}

.deepid-web-frame--fit {
  max-height: 70vh;
}
</style>
